import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardBody, Typography, Button, Spinner } from "@material-tailwind/react";
import { FaArrowLeft, FaPrint } from 'react-icons/fa';
import axios from 'axios';
import { rootUrl } from '../../../../config/config';

const DriverTransactionDetails = () => {
    const { type, doc_no } = useParams();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState(null);
    const [driverDetails, setDriverDetails] = useState(null);
    const [carDetails, setCarDetails] = useState(null);
    const [transactionType, setTransactionType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTransactionDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');

                // Fetch the specific transaction
                const transResponse = await axios.get(
                    `${rootUrl}/drivers/transactions/search?doc_no=${encodeURIComponent(doc_no)}&type=${encodeURIComponent(type)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!transResponse || !transResponse.data) {
                    setError('Transaction not found.');
                    return;
                }

                const transData = transResponse.data.data || transResponse.data;
                setTransaction(transData);

                // Fetch driver details if driver_id exists
                if (transData.driver_id) {
                    try {
                        const driverResponse = await axios.get(
                            `${rootUrl}/drivers?limit=1000`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const drivers = driverResponse.data.data || [];
                        const driver = drivers.find(d => d.id_no === transData.driver_id);
                        if (driver) {
                            setDriverDetails(driver);
                        }
                    } catch (err) {
                        console.error('Error fetching driver:', err);
                    }
                }

                // Fetch car details if plate_no exists
                if (transData.plate_no) {
                    try {
                        const carResponse = await axios.get(
                            `${rootUrl}/cars/${transData.plate_no}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (carResponse.data) {
                            setCarDetails(carResponse.data);
                        }
                    } catch (err) {
                        console.error('Error fetching car:', err);
                    }
                }

                // Fetch transaction type details
                try {
                    const typeResponse = await axios.get(
                        `${rootUrl}/payments/driver-types`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const types = typeResponse.data.data || typeResponse.data;
                    const foundType = types.find(t => t.type === type);
                    if (foundType) {
                        setTransactionType(foundType);
                    }
                } catch (err) {
                    console.error('Error fetching transaction types:', err);
                }

            } catch (err) {
                console.error('Error fetching transaction details:', err);
                setError(err.response?.data?.error || 'Failed to fetch transaction details.');
            } finally {
                setLoading(false);
            }
        };

        if (type && doc_no) {
            fetchTransactionDetails();
        }
    }, [type, doc_no]);

    const formatMoney = (value) => {
        if (value === null || value === undefined || value === '-') return '-';
        return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (e) {
            return dateString;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex justify-center items-center">
                <Spinner className="h-12 w-12" color="orange" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
                        {error}
                    </div>
                    <Button
                        onClick={() => navigate(-1)}
                        className="bg-gray-500"
                    >
                        Go Back
                    </Button>
                </div>
            </div>
        );
    }

    if (!transaction) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
                <div className="max-w-4xl mx-auto text-center text-gray-500">
                    Transaction not found
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
            <div className="max-w-6xl mx-auto">
                {/* Navigation */}
                <div className="print:hidden flex justify-between items-center mb-8">
                    <Button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 bg-gray-500"
                    >
                        <FaArrowLeft />
                        Back
                    </Button>
                    <Button
                        onClick={() => window.print()}
                        className="flex items-center gap-2 bg-[#FF5733]"
                    >
                        <FaPrint />
                        Print
                    </Button>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Driver Transaction Details</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        {transactionType?.long_desc || type} - Document #{doc_no}
                    </p>
                </div>

                {/* Transaction Information */}
                <Card className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <CardBody>
                        <div className="flex items-center mb-6">
                            <div className="w-2 h-8 bg-[#FF5733] rounded-full mr-3"></div>
                            <Typography variant="h4" color="blue-gray">
                                Transaction Information
                            </Typography>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Basic Details</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Transaction ID:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">{transaction.id}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Document Number:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">{transaction.doc_no}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Type:</span>
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold">
                                            {transaction.type}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Sub Type:</span>
                                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                            {transaction.sub_type || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Description:</span>
                                        <span className="font-semibold text-[#2a2e30]">{transaction.short_desc || transactionType?.short_desc || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Date:</span>
                                        <span className="font-semibold text-[#2a2e30]">{formatDate(transaction.date)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Week:</span>
                                        <span className="font-semibold text-[#2a2e30]">{transaction.week || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Financial Details</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Amount:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {formatMoney(transaction.amount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Credit (In):</span>
                                        <span className="font-mono font-semibold text-green-600">
                                            {formatMoney(transaction.credit)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Debit (Out):</span>
                                        <span className="font-mono font-semibold text-red-600">
                                            {formatMoney(transaction.debit)}
                                        </span>
                                    </div>
                                    {transaction.post_balance !== undefined && (
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                            <span className="text-gray-700 font-medium">Post Balance:</span>
                                            <span className="font-mono font-bold text-[#2a2e30]">
                                                {formatMoney(transaction.post_balance)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">References</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Your Reference:</span>
                                        <span className="font-mono text-[#2a2e30]">{transaction.your_ref || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Our Reference:</span>
                                        <span className="font-mono text-[#2a2e30]">{transaction.our_ref || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">System Information</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Input By:</span>
                                        <span className="font-semibold text-[#2a2e30]">{transaction.input_by || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Input Date:</span>
                                        <span className="font-semibold text-[#2a2e30]">{formatDate(transaction.input_date)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Edit By:</span>
                                        <span className="font-semibold text-[#2a2e30]">{transaction.edit_by || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Edit Date:</span>
                                        <span className="font-semibold text-[#2a2e30]">{formatDate(transaction.edit_date)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes Section */}
                        {transaction.notes && (
                            <div className="mt-6 bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Notes</h3>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{transaction.notes}</p>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Driver Information */}
                {driverDetails && (
                    <Card className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <CardBody>
                            <div className="flex items-center mb-6">
                                <div className="w-2 h-8 bg-[#FF5733] rounded-full mr-3"></div>
                                <Typography variant="h4" color="blue-gray">
                                    Driver Information
                                </Typography>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-base font-bold text-[#2a2e30] mb-3">Personal Details</h3>
                                    <div className="space-y-2.5 text-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Name:</span>
                                            <Link
                                                to={`/drivers/${driverDetails.id}`}
                                                className="font-semibold text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                                            >
                                                {driverDetails.driver_name || driverDetails.name || 'N/A'}
                                            </Link>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">ID Number:</span>
                                            <span className="font-mono font-semibold text-[#2a2e30]">
                                                {driverDetails.id_no || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Phone:</span>
                                            <span className="font-semibold text-[#2a2e30]">
                                                {driverDetails.phone || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-base font-bold text-[#2a2e30] mb-3">Financial Information</h3>
                                    <div className="space-y-2.5 text-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Current Balance:</span>
                                            <span className="font-mono font-semibold text-[#2a2e30]">
                                                {formatMoney(driverDetails.bal)}
                                            </span>
                                        </div>
                                        {driverDetails.bal_bf !== undefined && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700 font-medium">Balance B/F:</span>
                                                <span className="font-mono font-semibold text-[#2a2e30]">
                                                    {formatMoney(driverDetails.bal_bf)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Car Information */}
                {carDetails && (
                    <Card className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <CardBody>
                            <div className="flex items-center mb-6">
                                <div className="w-2 h-8 bg-[#FF5733] rounded-full mr-3"></div>
                                <Typography variant="h4" color="blue-gray">
                                    Vehicle Information
                                </Typography>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-base font-bold text-[#2a2e30] mb-3">Vehicle Details</h3>
                                    <div className="space-y-2.5 text-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Plate Number:</span>
                                            <Link
                                                to={`/cars/${carDetails.plate_no}`}
                                                className="font-bold text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                                            >
                                                {carDetails.plate_no}
                                            </Link>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Make:</span>
                                            <span className="font-semibold text-[#2a2e30]">{carDetails.make || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Model:</span>
                                            <span className="font-semibold text-[#2a2e30]">{carDetails.model || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Year:</span>
                                            <span className="font-semibold text-[#2a2e30]">{carDetails.year || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-base font-bold text-[#2a2e30] mb-3">Financial Details</h3>
                                    <div className="space-y-2.5 text-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Current Balance:</span>
                                            <span className="font-mono font-semibold text-[#2a2e30]">
                                                {formatMoney(carDetails.bal)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Balance B/F:</span>
                                            <span className="font-mono font-semibold text-[#2a2e30]">
                                                {formatMoney(carDetails.bal_bf)}
                                            </span>
                                        </div>
                                        {carDetails.weekly_amount && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700 font-medium">Weekly Amount:</span>
                                                <span className="font-mono font-semibold text-[#2a2e30]">
                                                    {formatMoney(carDetails.weekly_amount)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default DriverTransactionDetails;
